// Register Service Worker
export const registerServiceWorker = async (onUpdateAvailable) => {
  if ("serviceWorker" in navigator) {
    try {
      // Capture whether a controller already existed BEFORE we register.
      // If it did, any future controllerchange means an update (not first install).
      const hadController = !!navigator.serviceWorker.controller;

      // For iOS, add updateViaCache to bypass iOS caching
      const registration = await navigator.serviceWorker.register("/sw.js", {
        scope: "/",
        updateViaCache: "none", // Critical for iOS updates
      });
      console.log("Service Worker registered:", registration);

      // Auto-reload the page when a new service worker takes control.
      // Skipped on the very first install (no prior controller) so the user
      // isn't reloaded out of their initial session.
      let refreshing = false;
      navigator.serviceWorker.addEventListener("controllerchange", () => {
        if (refreshing) return;
        if (!hadController) return; // first install, nothing to reload over
        refreshing = true;
        reloadWhenIdle();
      });

      // Listen for service worker messages
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'SW_ACTIVATED') {
          console.log('Service Worker activated with version:', event.data.version);
        }
      });

      // Handle service worker updates
      registration.addEventListener("updatefound", () => {
        const newWorker = registration.installing;

        newWorker.addEventListener("statechange", () => {
          if (newWorker.state === "installed" && navigator.serviceWorker.controller) {
            // New service worker available
            console.log("New service worker available");
            if (onUpdateAvailable) {
              onUpdateAvailable(registration);
            }
          }
        });
      });

      // Check for updates periodically (iOS needs less aggressive checking)
      const updateInterval = 1800000; // 30 minutes (half an hour)
      setInterval(() => {
        console.log('Checking for service worker updates...');
        registration.update();
      }, updateInterval);

      return registration;
    } catch (error) {
      console.error("Service Worker registration failed:", error);
    }
  }
};

// Defer page reload while the app is doing something the user can't lose
// (e.g. a document upload). Components can set window.__appBusy = true.
function reloadWhenIdle() {
  const tryReload = () => {
    if (window.__appBusy) {
      console.log("App busy, deferring reload for SW update...");
      setTimeout(tryReload, 1000);
      return;
    }
    window.location.reload();
  };
  tryReload();
}

// Check if running on iOS
export const isIOS = () => {
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
};

// Check if app is installed
export const isPWAInstalled = () => {
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
};

// Install prompt
let deferredPrompt = null;

export const setupInstallPrompt = (onInstallPrompt) => {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    if (onInstallPrompt) {
      onInstallPrompt(true);
    }
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    if (onInstallPrompt) {
      onInstallPrompt(false);
    }
  });
};

export const showInstallPrompt = async () => {
  if (!deferredPrompt) {
    return false;
  }

  deferredPrompt.prompt();
  const { outcome } = await deferredPrompt.userChoice;
  deferredPrompt = null;
  return outcome === "accepted";
};

// Request notification permission
export const requestNotificationPermission = async () => {
  if (!("Notification" in window)) {
    console.log("Notifications not supported");
    return false;
  }

  if (Notification.permission === "granted") {
    return true;
  }

  if (Notification.permission !== "denied") {
    const permission = await Notification.requestPermission();
    return permission === "granted";
  }

  return false;
};

// Show notification
export const showNotification = (title, options = {}) => {
  if (Notification.permission === "granted") {
    if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.ready.then((registration) => {
        registration.showNotification(title, {
          icon: "/pwa-192x192.png",
          badge: "/pwa-192x192.png",
          vibrate: [200, 100, 200],
          ...options,
        });
      });
    } else {
      new Notification(title, {
        icon: "/pwa-192x192.png",
        ...options,
      });
    }
  }
};

// Activate waiting service worker
export const activateWaitingServiceWorker = (registration) => {
  if (registration && registration.waiting) {
    // Mark that we're updating
    sessionStorage.setItem('sw-just-updated', 'true');
    
    // Reload the page when the new service worker takes control
    let refreshing = false;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!refreshing) {
        refreshing = true;
        window.location.reload();
      }
    });
    
    // Tell the waiting service worker to activate
    registration.waiting.postMessage({ type: "SKIP_WAITING" });
  }
};

// Check if online
export const isOnline = () => navigator.onLine;

// Network status listeners
export const onNetworkStatusChange = (callback) => {
  window.addEventListener("online", () => callback(true));
  window.addEventListener("offline", () => callback(false));
};
